/**
 * Performance monitoring utilities
 * Provides helpers for tracking and optimizing application performance
 */

import { createLogger } from './logger';

const logger = createLogger('Performance');

/**
 * Debounce function to limit the rate of function execution
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle function to limit function execution to once per specified time period
 */
export function throttle<T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;

  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Debounce function with per-key independent timers
 * Each unique key gets its own debounce timer, preventing cross-key cancellations
 *
 * @example
 * const saveByKey = debounceByKey(
 *   (key: string, value: string) => api.save(key, value),
 *   1000
 * );
 * saveByKey('item1', 'data1'); // Timer 1 starts
 * saveByKey('item2', 'data2'); // Timer 2 starts (independent from Timer 1)
 * saveByKey('item1', 'data3'); // Timer 1 restarts (only affects item1)
 * saveByKey.cancel('item1');   // Timer 1 cleared (won't fire)
 * saveByKey.cancelAll();        // Every pending timer cleared
 *
 * @param func - Function to debounce (first parameter must be the key)
 * @param wait - Delay in milliseconds
 * @returns Debounced function with per-key timers + cancel/cancelAll
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface DebouncedByKey<T extends (...args: any[]) => unknown> {
  (...args: Parameters<T>): void;
  /** Cancel the pending timer for `key` (no-op if none pending). Returns true iff a timer was cleared. */
  cancel: (key: string) => boolean;
  /** Cancel every pending timer (does NOT fire any deferred call). */
  cancelAll: () => void;
  /** Fire the pending call for `key` immediately and clear its timer. Returns true iff a call fired. */
  flush: (key: string) => boolean;
  /**
   * Fire every pending call immediately. Use before teardown to persist
   * deferred work that would otherwise be lost to a cancel — e.g. a debounced
   * PATCH that must reach the server even when the user navigates away inside
   * the debounce window.
   */
  flushAll: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debounceByKey<T extends (...args: any[]) => unknown>(
  func: T,
  wait: number
): DebouncedByKey<T> {
  const timeouts = new Map<string, NodeJS.Timeout>();
  // Latest args per key, kept so `flush`/`flushAll` can fire the deferred call.
  const pendingArgs = new Map<string, Parameters<T>>();

  const debounced = ((...args: Parameters<T>) => {
    const key = String(args[0]); // First argument is the key
    pendingArgs.set(key, args);

    const later = () => {
      timeouts.delete(key);
      pendingArgs.delete(key);
      func(...args);
    };

    // Clear only the timer for THIS key (other keys unaffected)
    const existingTimeout = timeouts.get(key);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Set new timer for THIS key
    const newTimeout = setTimeout(later, wait);
    timeouts.set(key, newTimeout);
  }) as DebouncedByKey<T>;

  debounced.cancel = (key: string): boolean => {
    const existing = timeouts.get(key);
    pendingArgs.delete(key);
    if (!existing) return false;
    clearTimeout(existing);
    timeouts.delete(key);
    return true;
  };

  debounced.cancelAll = (): void => {
    for (const timeout of timeouts.values()) {
      clearTimeout(timeout);
    }
    timeouts.clear();
    pendingArgs.clear();
  };

  debounced.flush = (key: string): boolean => {
    const existing = timeouts.get(key);
    if (existing) {
      clearTimeout(existing);
      timeouts.delete(key);
    }
    const args = pendingArgs.get(key);
    pendingArgs.delete(key);
    if (!args) return false;
    func(...args);
    return true;
  };

  debounced.flushAll = (): void => {
    const entries = Array.from(pendingArgs.entries());
    for (const timeout of timeouts.values()) {
      clearTimeout(timeout);
    }
    timeouts.clear();
    pendingArgs.clear();
    for (const [, args] of entries) {
      func(...args);
    }
  };

  return debounced;
}

/**
 * Measures the execution time of an async function
 */
export async function measurePerformance<T>(
  name: string,
  fn: () => Promise<T>
): Promise<T> {
  if (typeof window === 'undefined') {
    // Skip performance monitoring on server
    return fn();
  }

  const startTime = performance.now();

  try {
    const result = await fn();
    const endTime = performance.now();
    const duration = endTime - startTime;

    // Log performance metrics in development
    if (process.env.NODE_ENV === 'development') {
      logger.debug(`${name}: ${duration.toFixed(2)}ms`);
    }

    // Send to analytics if needed
    if (window.performance && 'measure' in window.performance) {
      window.performance.measure(name, {
        start: startTime,
        end: endTime,
      });
    }

    return result;
  } catch (error) {
    const endTime = performance.now();
    const duration = endTime - startTime;

    if (process.env.NODE_ENV === 'development') {
      console.error(
        `[Performance] ${name} failed after ${duration.toFixed(2)}ms`
      );
    }

    throw error;
  }
}

/**
 * Lazy load images with Intersection Observer
 */
export function lazyLoadImage(
  imageElement: HTMLImageElement,
  src: string,
  placeholder?: string
): void {
  if (placeholder) {
    imageElement.src = placeholder;
  }

  const imageObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        imageElement.src = src;
        observer.unobserve(entry.target);
      }
    });
  });

  imageObserver.observe(imageElement);
}

/**
 * Check if code is running in production
 */
export const isProduction = process.env.NODE_ENV === 'production';

/**
 * Check if code is running on server
 */
export const isServer = typeof window === 'undefined';

/**
 * Get optimal image dimensions based on device pixel ratio
 */
export function getOptimalImageSize(
  baseWidth: number,
  baseHeight: number
): { width: number; height: number } {
  if (isServer) {
    return { width: baseWidth, height: baseHeight };
  }

  const dpr = window.devicePixelRatio || 1;
  const maxDpr = 2; // Cap at 2x to avoid huge images
  const actualDpr = Math.min(dpr, maxDpr);

  return {
    width: Math.round(baseWidth * actualDpr),
    height: Math.round(baseHeight * actualDpr),
  };
}

/**
 * Preload critical resources
 */
export function preloadResource(
  url: string,
  type: 'image' | 'script' | 'style'
): void {
  if (isServer) return;

  const link = document.createElement('link');
  link.rel = 'preload';
  link.href = url;

  switch (type) {
    case 'image':
      link.as = 'image';
      break;
    case 'script':
      link.as = 'script';
      break;
    case 'style':
      link.as = 'style';
      break;
  }

  document.head.appendChild(link);
}

/**
 * Check if user prefers reduced motion
 */
export function prefersReducedMotion(): boolean {
  if (isServer) return false;

  const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  return mediaQuery.matches;
}

/**
 * Get connection quality for adaptive loading
 */
export function getConnectionQuality(): 'slow' | 'medium' | 'fast' | 'unknown' {
  if (isServer) return 'unknown';

  interface NavigatorConnection {
    effectiveType?: string;
  }

  interface NavigatorWithConnection extends Navigator {
    connection?: NavigatorConnection;
    mozConnection?: NavigatorConnection;
    webkitConnection?: NavigatorConnection;
  }

  const nav = navigator as NavigatorWithConnection;
  const connection =
    nav.connection || nav.mozConnection || nav.webkitConnection;

  if (!connection) return 'unknown';

  const effectiveType = connection.effectiveType;

  switch (effectiveType) {
    case 'slow-2g':
    case '2g':
      return 'slow';
    case '3g':
      return 'medium';
    case '4g':
      return 'fast';
    default:
      return 'unknown';
  }
}

/**
 * Batch DOM updates using requestAnimationFrame
 */
export function batchUpdate(callback: () => void): void {
  if (isServer) {
    callback();
    return;
  }

  if ('requestAnimationFrame' in window) {
    window.requestAnimationFrame(callback);
  } else {
    setTimeout(callback, 16); // ~60fps
  }
}

/**
 * Create a memoized version of a function
 */
export function memoize<TArgs extends readonly unknown[], TReturn>(
  fn: (...args: TArgs) => TReturn,
  getKey?: (...args: TArgs) => string
): (...args: TArgs) => TReturn {
  const cache = new Map<string, TReturn>();

  return (...args: TArgs) => {
    const key = getKey ? getKey(...args) : JSON.stringify(args);

    if (cache.has(key)) {
      return cache.get(key)!;
    }

    const result = fn(...args);
    cache.set(key, result);

    // Limit cache size to prevent memory leaks
    if (cache.size > 100) {
      const firstKey = cache.keys().next().value;
      if (firstKey !== undefined) {
        cache.delete(firstKey);
      }
    }

    return result;
  };
}
